import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import Contacts from 'pages/dashboard/contacts/Contacts';
import ContactCreate from 'pages/dashboard/contacts/ContactCreate';
import CreateContactSite from 'pages/dashboard/contact-sites/CreateContactSite';
import ContactDetail from 'pages/dashboard/contacts/ContactDetail';
import ContactSites from 'pages/dashboard/contact-sites/ContactSites';
import Vehicles from 'pages/dashboard/vehicles/Vehicles';
import VehicleCreate from 'pages/dashboard/vehicles/VehicleCreate';
import Drivers from 'pages/dashboard/drivers/Drivers';
import DriverCreate from 'pages/dashboard/drivers/DriverCreate';
import Transactions from 'pages/dashboard/transactions/transactions';
import Companies from 'pages/dashboard/companies/Companies';
import CompanyCreate from 'pages/dashboard/companies/CreateCompany';
import TransactionReports from 'pages/dashboard/transactionReports/transactionReports';
import AddEditTransaction from 'pages/dashboard/transactions/AddEditTransactions';
import TransactionPdfViewer from 'pages/dashboard/transactions/transactionPdfViewer';
import CameraFeedPage from 'pages/dashboard/CameraFeedPage';

// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  console.log('pathname:', pathname);
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        }
      ]
    },

    // Dashboard Routes

    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <Navigate to="/dashboard/jobs/list" replace /> }]
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),

      children: [
        {
          path: 'jobs',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace /> },
            { path: 'shop', element: <JobsList /> },
            { path: 'list', element: <JobsList /> }
          ]
        },
        { element: <Navigate to="/dashboard/user/list" replace /> },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/list" replace /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> }
          ]
        },
        {
          path: 'contacts',
          children: [
            { element: <Navigate to="/dashboard/contacts/list" replace /> },
            { path: 'list', element: <Contacts /> },
            { path: 'new', element: <ContactCreate /> },
            { path: 'edit/:id', element: <ContactCreate /> },
            { path: 'details/:id', element: <ContactDetail /> }
          ]
        },
        {
          path: 'sites',
          children: [
            { element: <Navigate to="/dashboard/sites/list" replace /> },
            { path: 'list', element: <ContactSites /> },
            { path: 'new/:clientId', element: <CreateContactSite /> },
            { path: 'new', element: <CreateContactSite /> },
            { path: 'edit/:id', element: <CreateContactSite /> },
            { path: 'details/:id', element: <CreateContactSite /> }
          ]
        },
        {
          path: 'vehicles',
          children: [
            { element: <Navigate to="/dashboard/vehicles/list" replace /> },
            { path: 'list', element: <Vehicles /> },
            { path: 'new', element: <VehicleCreate /> },
            { path: 'edit/:id', element: <VehicleCreate /> },
            { path: 'details/:id', element: <VehicleCreate /> }
          ]
        },
        {
          path: 'drivers',
          children: [
            { element: <Navigate to="/dashboard/drivers/list" replace /> },
            { path: 'list', element: <Drivers /> },
            { path: 'new', element: <DriverCreate /> },
            { path: 'edit/:id', element: <DriverCreate /> },
            { path: 'details/:id', element: <DriverCreate /> }
          ]
        },
        {
          path: 'transactions',
          children: [
            { element: <Navigate to="/dashboard/transactions/list" replace /> },
            { path: 'list', element: <Transactions /> },
            { path: 'edit/:id', element: <AddEditTransaction /> },
            { path: 'new', element: <AddEditTransaction /> },
            { path: 'view/:id', element: <TransactionPdfViewer /> }
          ]
        },
        {
          path: 'reports',
          children: [
            { element: <Navigate to="/dashboard/reports/list" replace /> },
            { path: 'list', element: <TransactionReports /> }
          ]
        },
        {
          path: 'cameras',
          children: [
            { element: <Navigate to="/dashboard/cameras/list" replace /> },
            { path: 'list', element: <CameraFeedPage /> }
          ]
        },
        {
          path: 'companies',
          children: [
            { element: <Navigate to="/dashboard/companies/list" replace /> },
            { path: 'list', element: <Companies /> },
            { path: 'new', element: <CompanyCreate /> },
            { path: 'edit/:id', element: <CompanyCreate /> },
            { path: 'details/:id', element: <CompanyCreate /> }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },

    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
// Dashboard

const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
// added tablelist component:
const TableList = Loadable(lazy(() => import('../pages/dashboard/TableList')));

const JobsList = Loadable(lazy(() => import('../pages/dashboard/Jobslist')));

const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/Jobslist')));

// Main
// const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
